import { SystemEntity } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { getEntityIcon, getEntityDisplayName } from '../utils/index';
import { SystemEntityPreview } from '../preview-ui';
import { SupplierInline } from './supplier-inline';
import { CustomerInline } from './customer-inline';
import { PurchaseOrderInline } from './purchase-order-inline';
import { PurchaseOrderLineItemInline } from './purchase-order-line-item-inline';
import { FinancialTransactionInline } from './financial-transaction-inline';
import { APInvoiceInline } from './ap-invoice-inline';
import { OutboundEmailInline } from './outbound-email-inline';
import { ChargeInline } from './charge-inline';
import { SalesOrderInline } from './sales-order-inline';
import { ShipmentInline } from './shipment-inline';
import { InboundEmailInline } from './inbound-email-inline';
import { ExternalSystemReferenceInline } from './external-system-reference-inline';
import { TransferOrderInline } from './transfer-order-inline';
import { ARInvoiceInline } from './ar-invoice-inline';
import { ARPaymentInline } from './ar-payment-inline';

interface SystemEntityInlineProps {
  entityType: SystemEntity;
  entityId: string;
  className?: string;
}

function FallbackInline({
  entityType,
  entityId,
  className,
}: SystemEntityInlineProps) {
  const Icon = getEntityIcon(entityType);
  const displayName = getEntityDisplayName(entityType);

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <div className="inline-block">
          <Badge
            variant="outline"
            className={cn('gap-1.5 cursor-pointer', className)}
          >
            <Icon className="size-3" />
            <span>{displayName}</span>
          </Badge>
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-auto p-0" align="start">
        <SystemEntityPreview entityType={entityType} entityId={entityId} />
      </HoverCardContent>
    </HoverCard>
  );
}

/**
 * Component renderers for each system entity type
 * Maps entity types to their inline UI representations
 */
const entityRenderers: Partial<
  Record<SystemEntity, (props: SystemEntityInlineProps) => React.ReactNode>
> = {
  [SystemEntity.AP_SUPPLIER]: (props) => <SupplierInline {...props} />,
  [SystemEntity.AR_CUSTOMER]: (props) => <CustomerInline {...props} />,
  [SystemEntity.INTERNAL_PURCHASE_ORDER]: (props) => (
    <PurchaseOrderInline {...props} />
  ),
  [SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM]: (props) => (
    <PurchaseOrderLineItemInline {...props} />
  ),
  [SystemEntity.FINANCIAL_TRANSACTION]: (props) => (
    <FinancialTransactionInline {...props} />
  ),
  [SystemEntity.AP_INVOICE]: (props) => <APInvoiceInline {...props} />,
  [SystemEntity.OUTBOUND_EMAIL]: (props) => <OutboundEmailInline {...props} />,
  [SystemEntity.CHARGE]: ({ entityId, className }) => (
    <ChargeInline entityId={entityId} className={className} />
  ),
  [SystemEntity.AR_INVOICE]: (props) => <ARInvoiceInline {...props} />,
  [SystemEntity.AR_PAYMENT]: (props) => <ARPaymentInline {...props} />,
  [SystemEntity.AR_SALES_ORDER]: (props) => <SalesOrderInline {...props} />,
  [SystemEntity.SCM_SHIPMENT]: (props) => <ShipmentInline {...props} />,
  [SystemEntity.INBOUND_EMAIL]: (props) => <InboundEmailInline {...props} />,
  [SystemEntity.EXTERNAL_SYSTEM_REFERENCE]: (props) => (
    <ExternalSystemReferenceInline {...props} />
  ),
  [SystemEntity.IM_TRANSFER_ORDER]: (props) => (
    <TransferOrderInline {...props} />
  ),
};

/**
 * Inline UI component for system entities
 * Renders a badge-based inline representation
 */
export function SystemEntityInline({
  entityType,
  entityId,
  className,
}: SystemEntityInlineProps) {
  const normalizedEntityType = entityType as SystemEntity;
  const Renderer = entityRenderers[normalizedEntityType];

  if (Renderer) {
    return (
      <>{Renderer({ entityType: normalizedEntityType, entityId, className })}</>
    );
  }

  return (
    <FallbackInline
      entityType={normalizedEntityType}
      entityId={entityId}
      className={className}
    />
  );
}
